import {
  CommerceEngineDefinitionOptions,
  defineInstantProducts,
  defineParameterManager,
  defineProductList,
  defineStandaloneSearchBox,
  defineContext,
  defineSort,
  defineFacetGenerator,
  definePagination,
  defineSummary,
} from "@coveo/headless-react/ssr-commerce";
import { debug, NativeDataFetcher } from "@sitecore-jss/sitecore-jss";
import { MapParams } from "../../types/map-types";

export interface SearchTokenResponse {
  token: string;
}

export const getAccessToken = async (): Promise<string> => {
  debug.common("getSearchToken-Refreshing");
  const data = {
    userIds: [
      {
        name: "anonymous",
        provider: "Email Security Provider",
      },
    ],
  };

  const fetcher = new NativeDataFetcher({
    headers: [
      ["Content-Type", "application/json"],
      ["Accept", "application/json"],
      ["Authorization", `Bearer ${process.env.CoveoApiKey ?? ""}`],
    ],
  });

  const orgId = process.env.NEXT_PUBLIC_CoveoOrganizationId ?? "";

  return await fetcher
    .post<SearchTokenResponse>(
      `https://${orgId}.org.coveo.com/rest/search/v2/token?organizationId=${orgId}`,
      data,
    )
    .then((response) => {
      if (!response?.data?.token) {
        debug.http("getSearchToken-Error", response);
        return "";
      }
      return response.data.token;
    })
    .catch((e) => {
      debug.http(e);
      return "";
    });
};

/*

/**
 * Coveo doesn't accept localhost urls in the listing config UI so local environments need to specify a different search host
 * that Coveo will accept (NEXT_PUBLIC_CoveoSearchHost)
 * @returns string
 */
export const getCoveoSearchHost = (): string => {
  if (process.env.NEXT_PUBLIC_CoveoSearchHost) {
    return process.env.NEXT_PUBLIC_CoveoSearchHost;
  } else {
    return "";
  }
};

export default {
  configuration: {
    organizationId: process.env.NEXT_PUBLIC_CoveoOrganizationId ?? "",
    accessToken: process.env.NEXT_PUBLIC_CoveoSearchToken ?? "",
    renewAccessToken: getAccessToken,
    analytics: {
      trackingId: process.env.NEXT_PUBLIC_CoveoTrackingId ?? "",
      enabled: false, // Disabled by default, will be enabled based on consent
    },
    context: {
      language: "en",
      country: "US",
      currency: "USD",
      view: {
        url: `${getCoveoSearchHost()}/`,
      },
    },
    preprocessRequest: (request: any) => {
      let body = JSON.parse(request.body);

      body.context.environment =
        process.env.NEXT_PUBLIC_CoveoEnvironment ?? "production";

      const viewUrl = new URL(body.context.view.url);

      const bb = viewUrl.searchParams.get("bb") ?? null;

      if (bb) {
        const mapParams: MapParams = JSON.parse(atob(bb));

        if (mapParams.sw && mapParams.ne) {
          // tell the coveo query pipeline that we want to filter results using a bounding box
          body.context.hasBounds = true;
          body.context.llat = mapParams.sw.lat;
          body.context.llng = mapParams.sw.lng;
          body.context.hlat = mapParams.ne.lat;
          body.context.hlng = mapParams.ne.lng;
        }

        // remove any query string params, we only need them to get the new bounding box and coveo listing configs
        // will not match if we include the query params
        const newViewUrl = `${getCoveoSearchHost()}${viewUrl.pathname}`;

        body.context.view.url = newViewUrl;
      }

      // map scroll is effectively a new search so return to the first page withour triggering a new search
      const resetPagination = viewUrl.searchParams.get("resetPagination");

      if (resetPagination && resetPagination === "true") {
        body.page = 0;
      }

      request.body = JSON.stringify(body);
      return request;
    },
  },
  controllers: {
    context: defineContext(),
    instantProducts: defineInstantProducts(),
    pagination: definePagination(),
    summary: defineSummary(),
    standaloneSearchBox: defineStandaloneSearchBox({
      options: { redirectionUrl: "/s" },
    }),
    parameterManager: defineParameterManager(),
    productList: defineProductList(),
    sort: defineSort(),
    facetGenerator: defineFacetGenerator(),
  },
} satisfies CommerceEngineDefinitionOptions as CommerceEngineDefinitionOptions;
